import { LoggerService } from '@angular-ru/cdk/logger';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ISocialSharing } from 'src/app/api/modules/core/dynamic/components/ISocialSharing';
import { ISocialSharingButton } from 'src/app/api/modules/core/dynamic/components/ISocialSharingButton';
import { SocialSharingButtonComponent } from '../social-sharing-button/social-sharing-button.component';

/**
 * The Social Sharing Component is responsible for displaying a group of social buttons or links.
 *
 * It can behave in 2 ways:
 *
 * 1. It displays a row of `link` to homepages such as `https://twitter.com/Twitter`
 * 2. It displays a row of `share` buttons to actually post content.
 *
 * You cannot mix and match these within the same container.
 */
@Component({
  selector: 'app-social-sharing',
  templateUrl: './social-sharing.component.html',
  styleUrls: ['./social-sharing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SocialSharingButtonComponent],
})
export class SocialSharingComponent {
  private readonly deviceDetector = inject(DeviceDetectorService);
  private readonly logger = inject(LoggerService);

  /**
   * The Input Configuration
   */
  readonly config = input.required<ISocialSharing>();

  /**
   * The type of group being displayed, defaulting to `share` when the
   * configuration omits it.
   */
  protected readonly type = computed(() => this.config().type || 'share');

  /**
   * The buttons to display after capability filtering:
   *
   * 1. Some platforms only have homepages and cannot share content.
   * 2. Some platforms only have mobile applications, so their share deep-links
   *    are dead ends on desktop. Homepage links are plain URLs and always work.
   */
  protected readonly buttons = computed<ISocialSharingButton[]>(() => {
    const componentArray = this.config().componentArray;
    if (!componentArray?.length) {
      return [];
    }

    const type = this.type();

    //  some social entities only support homepages
    let filtered = componentArray.filter((shareData) => {
      switch (shareData.platform) {
        case 'twitch':
        case 'tiktok':
          if (type === 'share') {
            this.logger.warn(`[Social Sharing] - the ${shareData.platform} platform does not support content sharing.`);
            return false;
          }
      }
      return true;
    });

    // some social entries don't support desktop
    if (!this.deviceDetector.isMobile()) {
      filtered = filtered.filter((shareData) => {
        switch (shareData.platform) {
          case 'instagram':
          case 'sms':
          case 'viber':
          case 'facebook-messenger':
            // homepage types are always allowed though since they are just links
            if (type !== 'homepage') {
              return false;
            }
        }
        return true;
      });
    }

    return filtered;
  });
}
